import { DataTableController } from 'components/common'
import ActiveFilterChip from 'components/common/activeFilterChip'
import { ImportColumn } from 'components/common/importModal'
import { columns } from './utils'
import { usePageAccess, useQuery } from 'hooks'
import { useEffect, useState } from 'react'
import {
  exportEcomPricingData,
  importEcomPricingData,
} from 'utils/api/ecomPricingApi'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'
import EcomPricingDataTable from './ecomPricingTable'
import EcomPricingFilters from './ecomPricingFilters'
import { FILTER_FIELD_ECOM_PRICING } from 'components/orderManagement'
import toast from 'react-hot-toast'
import { Helmet } from 'react-helmet'
import { AxiosError } from 'axios'
import { filterEmptyValues } from 'utils/helpers'

const EcomPricing = () => {
  const [showFilter, setShowFilter] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [importColumns, setImportColumns] = useState<ImportColumn[]>([])
  const { getQuery } = useQuery()

  useEffect(() => {
    setSelectedColumns([])
    setLoading(false)
  }, [])

  useEffect(() => {
    getImportTemplate('ecomPricing')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadSample = async () => {
    const response = await downloadImportSample('ecomPricing')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'ecom_pricing_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const { isEditor } = usePageAccess()

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }

  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_ECOM_PRICING)

    // if not filters found in URL
    if (!filters) {
      return toast.error('Filters are required to export data!', {
        duration: 3000,
      })
    }

    // filtering empty values and keys of filter
    const pricingFilter = filterEmptyValues(filters)

    // if filters are present with empty values
    if (Object.keys(pricingFilter).length <= 0) {
      return toast.error('Please select at least on filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await exportEcomPricingData({ filters: pricingFilter })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }

      toast.error('Failed to export Pricing Data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | ECOM Pricing</title>
      </Helmet>

      <DataTableController
        isEditor={isEditor}
        columns={columns}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleImport={importEcomPricingData}
        handleExport={handleExport}
        activeColumnKey="ecompricing"
        placeholder="Barcode and Cluster"
        defaultSelectedColumns={selectedColumns}
        loading={loading}
        importColumns={importColumns}
        importModuleName="ecom pricing"
        onDownloadSample={handleDownloadSample}
      />

      {!showFilter && (
        <ActiveFilterChip filterKey={FILTER_FIELD_ECOM_PRICING} />
      )}

      {showFilter ? (
        <EcomPricingFilters toggleFilterForm={toggleFilterForm} />
      ) : (
        <EcomPricingDataTable
          isEditor={isEditor}
          setSelectedColumns={setSelectedColumns}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  )
}

export default EcomPricing
